import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'shared/services/redux/hooks/use-selector';
import { scheduleActions, scheduleSelectors } from 'features/schedule/redux';
import { ScheduleReportStatus } from 'services/api';
import { getSlotKey } from 'features/schedule/utils';
import { useAppDispatch } from 'shared/services/redux/hooks/use-dispatch';
import type { ScheduleViolationCategory, ScheduleViolationPanelLesson } from 'shared/api';
import type { MaybeType } from 'shared/types';

export const useViolationsDrawer = (open: boolean, type: MaybeType<ScheduleReportStatus>) => {
  const dispatch = useAppDispatch();

  const scheduleViolations = useAppSelector(scheduleSelectors.getViolations);

  const [activeKeys, setActiveKeys] = useState<string[]>([]);

  const category: ScheduleViolationCategory = useMemo(
    () => (type === ScheduleReportStatus.CRITICAL ? 'LOGICAL' : 'SANPIN'),
    [type],
  );

  const violationsByCategory = useMemo(
    () => scheduleViolations.filter((violation) => violation.category === category),
    [type, scheduleViolations],
  );

  const togglePanelKey = useCallback((panelKey: string) => {
    setActiveKeys((prev) =>
      prev.includes(panelKey) ? prev.filter((k) => k !== panelKey) : [...prev, panelKey],
    );
  }, []);

  const handleViolationLessonClick = (lesson: ScheduleViolationPanelLesson) => {
    const slotKey = getSlotKey(
      lesson.dayOfWeek,
      lesson.startTime,
      lesson.endTime,
      lesson.educationalClass.id,
    );

    dispatch(
      scheduleActions.setSelectedLesson({
        lesson: {
          id: lesson.lessonId,
          subject: lesson.subject,
          teacher: lesson.teacher,
          room: lesson.room,
          pinned: lesson.pinned,
        },
        slotKey,
        educationalClass: lesson.educationalClass,
        detailOpen: false,
      }),
    );

    requestAnimationFrame(() => {
      const element = document.querySelector<HTMLElement>(`[data-lesson-id="${lesson.lessonId}"]`);

      const tableScroll = document.querySelector<HTMLElement>('[data-schedule-table-scroll]');

      if (!element || !tableScroll) return;

      const tableRect = tableScroll.getBoundingClientRect();
      const elementRect = element.getBoundingClientRect();

      const offset =
        elementRect.left + elementRect.width / 2 - (tableRect.left + tableRect.width / 2);

      tableScroll.scrollTo({ left: tableScroll.scrollLeft + offset, behavior: 'auto' });
      element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    });
  };

  useEffect(() => {
    if (!open) {
      setActiveKeys([]);
      dispatch(scheduleActions.setSelectedLesson(null));
    }
  }, [open]);

  return {
    state: {
      violations: violationsByCategory,
      activeKeys,
    },
    methods: {
      togglePanelKey,
      handleViolationLessonClick,
    },
  };
};
